import './AddNotePopup.scss'
import CheckBox from "devextreme-react/check-box";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import TagBox from "devextreme-react/tag-box";
import TextArea from "devextreme-react/text-area";
import TextBox from "devextreme-react/text-box";
import ValidationSummary from "devextreme-react/validation-summary";
import Validator, { EmailRule, RequiredRule } from "devextreme-react/validator";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../contexts/auth";
import { Field } from "../../../../layouts";
import { NoteType } from "../../../../types/enums";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { apiAddOrderNote } from "../api/add-order-note";
import { apiEmailOrderNote } from "../api/email-order-note";
import { useGetNoteRecipients } from "../api/get-note-recipients";
import { AddNotePopupProps } from "../types/AddNotePopupProps";
import { OrderNote } from "../types/OrderNote";
import { SelectBox } from 'devextreme-react/select-box';
import { LoadPopupPanel } from '../../../../components';

export default function AddNotePopup(props: AddNotePopupProps) {

	const [isEmailPopupVisible, setEmailPopupVisibility] = useState(false);
	const [internalPortal, setInternalPortal] = useState(false);
	const [emailsTo, setEmailsTo] = useState<string[]>([]);
	const [urgent, setUrgent] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [noteType, setNoteType] = useState(props.params.noteTypeID ?? NoteType.General);
	const [emailSubject, setEmailSubject] = useState(props.params.emailSubject);
	const { user } = useAuth();

	const recipients = useGetNoteRecipients(props.params.companyIDs ?? [], props.params.kindOfOrder,
		!props.params.onEmailNote && isEmailPopupVisible);

	useEffect(() => {
		if (recipients.data) {
			setEmailsTo(recipients.data.defaultEmailAddresses);
		}
	}, [recipients.data]);

	const noteTextRef = useRef<TextArea>(null);
	const emailNoteTextRef = useRef<TextArea>(null);

	return (
		<>
			<Popup
				title={props.caption}
				visible={true}
				hideOnOutsideClick={true}
				onHiding={() => { props.onHidePopup(); }}
				width={600}
				height={520}
			>
				<ToolbarItem
					options={{
						icon: 'dx-icon dx-icon-close',
						onClick: () => props.onHidePopup()
					}}
					widget="dxButton"
					location="after"
				/>

				<ValidationSummary validationGroup='noteValidationGroup'></ValidationSummary>
				{
					props.params.showNoteType &&
					<Field label='Note Type' className='black-label'>
						<SelectBox
							value={noteType}
							onValueChanged={(e) => setNoteType(e.value)}
							items={[{ text: 'General', value: NoteType.General }, { text: 'Change Request', value: NoteType.ChangeRequest }]}
							valueExpr='value'
							displayExpr='text'
							showClearButton={false}
						/>
					</Field>
				}

				<TextArea inputAttr={{ 'data-testid': 'txt-note' }}
					ref={noteTextRef}
					height={230}
				>
					<Validator validationGroup="noteValidationGroup">
						<RequiredRule message={'Note Text is required'} />
					</Validator>
				</TextArea>

				<CheckBox elementAttr={{ 'data-testid': 'chb-internal-portal' }}
					text='Internal Portal'
					className="dx-fieldset"
					value={internalPortal}
					onValueChanged={(e) => setInternalPortal(e.value)} />

				<ToolbarItem
					options={{
						disabled: processing,
						validationGroup: 'noteValidationGroup',
						type: 'normal',
						stylingMode: 'contained',
						text: 'Add Note',
						elementAttr: { 'data-testid': 'btn-add-note' },
						onClick: (e: any) => {
							let validation = e.validationGroup.validate();
							if (validation.isValid) {
								const newNote: OrderNote = {
									noteID: -(props.params.temporaryNoteCount ?? 0) - 1,
									noteText: noteTextRef.current?.instance.option("value") as string,
									internalPortal: internalPortal,
									noteType: noteType,
									createdBy: user?.userName!,
									createdOn: new Date()
								};

								if (props.params.orderID) {
									setProcessing(true);

									apiAddOrderNote(props.params.orderID, props.params.kindOfOrder, newNote)
										.then((result) => {
											if (!result.isSuccess) {
												showErrorMessage(result.errorMessage!);
											}
											else {
												props.onHidePopup();
												showSuccessMessage("The note has been added");
												noteTextRef.current?.instance.option("value", '');
												setInternalPortal(false);
												e.validationGroup.reset();
												props.params.onNoteAdded(newNote);
											}
										})
										.finally(() => setProcessing(false))
								}
								else {
									props.onHidePopup();
									noteTextRef.current?.instance.option("value", '');
									setInternalPortal(false);
									e.validationGroup.reset();
									props.params.onNoteAdded(newNote);
								}
							}
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				/>
				{
					<ToolbarItem
						options={{
							type: 'normal',
							stylingMode: 'contained',
							text: 'Email Note',
							elementAttr: { 'data-testid': 'btn-email-note' },
							onClick: () => {
								if (props.params.onEmailNote) {
									props.params.onEmailNote(noteTextRef.current?.instance.option("value") ?? '');
									props.onHidePopup();
								}
								else {
									setEmailPopupVisibility(true);
								}
							}
						}}
						toolbar={'bottom'}
						widget="dxButton"
						location="after"
					/>
				}

				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Cancel',
						elementAttr: { 'data-testid': 'btn-cancel' },
						onClick: () => props.onHidePopup()
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				/>
				<LoadPopupPanel visible={processing} />
			</Popup>
			{
				!props.params.onEmailNote &&
				<Popup
					title="Email Note"
					visible={isEmailPopupVisible}
					hideOnOutsideClick={true}
					onHiding={() => {
						setEmailPopupVisibility(false); props.onHidePopup();
					}}
					width={600}
					height={580}
				>
					<ToolbarItem
						options={{
							icon: 'dx-icon dx-icon-close',
							onClick: () => {
								setEmailPopupVisibility(false); props.onHidePopup();
							}
						}}
						widget="dxButton"
						location="after"
					/>

					<ValidationSummary validationGroup='emailNoteValidationGroup'></ValidationSummary>
					<div className='email-note-form'>
						<Field label="Subject">
							<TextBox value={emailSubject} onValueChanged={(e) => setEmailSubject(e.value)}>
								<Validator validationGroup="emailNoteValidationGroup">
									<RequiredRule message={'Subject is required'} />
								</Validator>
							</TextBox>
						</Field>

						<Field label="To">
							<TagBox
								value={emailsTo}
								onValueChanged={(e) => setEmailsTo(e.value)}
								dataSource={recipients.data?.emailAddresses}
								applyValueMode={'instantly'}
								acceptCustomValue={true}
								onCustomItemCreating={(e) => {
									if (e.text) {
										e.customItem = e.text;
										setEmailsTo(emailsTo.concat([e.text!]));
									}
								}}
								onSelectionChanged={(e) => {
									if (e.addedItems.length > 0) {
										setTimeout(() => { e.component.open(); }, 0);
									}
								}}
							>
								<Validator validationGroup="emailNoteValidationGroup">
									<RequiredRule message={'To is required'} />
									<EmailRule message={'Invalid email address'} />
								</Validator>
							</TagBox>
						</Field>

						<Field label="Urgent">
							<CheckBox value={urgent} onValueChanged={(e) => setUrgent(e.value)} />
						</Field>
						{
							props.params.showNoteType &&
							<Field label='Note Type'>
								<SelectBox
									value={noteType}
									onValueChanged={(e) => setNoteType(e.value)}
									items={[{ text: 'General', value: NoteType.General }, { text: 'Change Request', value: NoteType.ChangeRequest }]}
									valueExpr='value'
									displayExpr='text'
									showClearButton={false}
								/>
							</Field>
						}
						<TextArea
							ref={emailNoteTextRef}
							height={180}
						>
							<Validator validationGroup="emailNoteValidationGroup">
								<RequiredRule message={'Note Text is required'} />
							</Validator>
						</TextArea>
					</div>
					<ToolbarItem
						options={{
							disabled: processing,
							validationGroup: 'emailNoteValidationGroup',
							type: 'normal',
							stylingMode: 'contained',
							text: 'Email Note',
							onClick: (e: any) => {
								let validation = e.validationGroup.validate();
								if (validation.isValid) {
									const newNote: OrderNote = {
										noteID: -(props.params.temporaryNoteCount ?? 0) - 1,
										noteText: emailNoteTextRef.current?.instance.option("value") as string,
										emailSubject: emailSubject,
										toEmailAddress: emailsTo.join(", "),
										urgent: urgent,
										noteType: noteType,
										createdBy: user?.userName!,
										createdOn: new Date()
									};

									if (props.params.orderID) {
										setProcessing(true);

										apiEmailOrderNote(props.params.orderID, props.params.kindOfOrder, newNote)
											.then((result) => {
												if (!result.isSuccess) {
													showErrorMessage(result.errorMessage!);
												}
												else {
													setEmailPopupVisibility(false);
													props.onHidePopup();
													showSuccessMessage("The note has been emailed");
													emailNoteTextRef.current?.instance.option("value", '');
													setUrgent(false);
													e.validationGroup.reset();
													props.params.onNoteAdded(newNote);
												}
											})
											.finally(() => setProcessing(false))
									}
									else {
										setEmailPopupVisibility(false);
										props.onHidePopup();
										emailNoteTextRef.current?.instance.option("value", '');
										setUrgent(false);
										e.validationGroup.reset();
										props.params.onNoteAdded(newNote);
									}
								}
							}
						}}
						toolbar={'bottom'}
						widget="dxButton"
						location="after"
					/>
					<ToolbarItem
						options={{
							type: 'normal',
							stylingMode: 'contained',
							text: 'Cancel',
							onClick: () => {
								setEmailPopupVisibility(false); props.onHidePopup();
							}
						}}
						toolbar={'bottom'}
						widget="dxButton"
						location="after"
					/>
					<LoadPopupPanel visible={processing || recipients.isLoading} />
				</Popup>
			}

		</>
	)
}
